import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const schemaData = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/blog/home-health-compliance-tips#article`,
  url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/blog/home-health-compliance-tips`,
  headline: 'Top 10 Compliance Tips Every Home Health Agency Must Know in 2026',
  description: 'Staying compliant in the home health industry is more critical than ever. Discover the top compliance strategies that protect your agency from costly penalties and survey failures.',
  image: 'https://public.readdy.ai/ai/img_res/6d926a92a16f5c40af01e86adcd1e69f.jpg',
  datePublished: '2026-02-28',
  dateModified: '2026-02-28',
  author: {
    '@type': 'Person',
    name: 'Dr. Osmel Villarreal',
    jobTitle: 'ACHC Certified Consultant, DBA, MBA, MS',
    url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/why-dr-villarreal`,
  },
  publisher: {
    '@type': 'Organization',
    name: 'Advantixx Home Health Accreditation Consultants',
    url: import.meta.env.VITE_SITE_URL || 'https://example.com',
  },
  isPartOf: { '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/blog#webpage` },
  keywords: ['home health compliance', 'ACHC compliance tips', 'CMS regulations', 'survey readiness', 'compliance audit'],
  articleSection: 'Compliance',
  wordCount: 1500,
};

const tips = [
  { num: '01', title: 'Conduct Regular Internal Compliance Audits', desc: 'Do not wait for a surveyor to find your gaps. Schedule quarterly internal audits of clinical records, personnel files, and operational processes. Document findings and corrective actions to demonstrate a culture of continuous improvement.' },
  { num: '02', title: 'Keep Policies & Procedures Current', desc: 'Outdated policies are one of the top reasons agencies fail surveys. Review and update all policies at least annually — or whenever regulations change. Ensure every policy has a review date, approval signature, and version number.' },
  { num: '03', title: 'Train Staff Continuously — Not Just at Onboarding', desc: 'Compliance training must be ongoing. Implement monthly in-services, competency checks, and documentation reviews. Track all training in personnel files with dates, topics, and staff signatures.' },
  { num: '04', title: 'Maintain Accurate and Complete Clinical Records', desc: 'Clinical documentation is the backbone of compliance. Every visit note, care plan, physician order, and OASIS assessment must be complete, timely, and accurate. Implement a documentation audit process to catch errors before surveyors do.' },
  { num: '05', title: 'Implement a Robust QAPI Program', desc: 'A Quality Assessment and Performance Improvement (QAPI) program is required by CMS and ACHC. Track key performance indicators, analyze trends, and implement improvement projects. Document everything — surveyors want to see evidence of your QAPI activities.' },
  { num: '06', title: 'Ensure Emergency Preparedness Plans Are Actionable', desc: 'Your emergency preparedness plan must be more than a document on a shelf. Conduct annual drills, train all staff on their roles, and update the plan to reflect current risks and resources in your service area.' },
  { num: '07', title: 'Verify All Staff Credentials and Background Checks', desc: 'Every employee and contractor must have current licenses, certifications, and background checks on file. Implement a tracking system with automatic renewal reminders to prevent lapses that could jeopardize your accreditation.' },
  { num: '08', title: 'Manage Infection Control Proactively', desc: 'Infection control is a high-priority area for surveyors. Ensure staff are trained on standard precautions, PPE use, and reporting procedures. Track infection rates and implement corrective actions when thresholds are exceeded.' },
  { num: '09', title: 'Establish a Clear Complaint and Grievance Process', desc: 'Patients and families must know how to file complaints, and your agency must have a documented process for investigating and resolving them. Track all complaints, document outcomes, and use findings to improve care.' },
  { num: '10', title: 'Partner With an ACHC Certified Consultant', desc: 'The most effective way to ensure compliance is to work with an expert who knows ACHC standards inside and out. An ACHC Certified Consultant like Dr. Osmel Villarreal can identify gaps you might miss and provide proven strategies to address them before they become survey findings.' },
];

export default function HomeHealthComplianceTips() {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-compliance-tips';
    script.text = JSON.stringify(schemaData);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('schema-compliance-tips');
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0d0d0d] font-['Montserrat',sans-serif]">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-36 pb-0">
        <div className="relative h-[420px] md:h-[520px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=healthcare%20compliance%20checklist%20on%20a%20desk%20with%20a%20stethoscope%20and%20pen%2C%20dark%20elegant%20background%20with%20gold%20tones%2C%20professional%20medical%20office%20setting&width=1400&height=600&seq=blog2hero&orientation=landscape"
            alt="Home Health Compliance Tips 2026"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-[#0d0d0d]/50 to-[#0d0d0d]/20"></div>
          <div className="absolute bottom-0 left-0 right-0 max-w-4xl mx-auto px-6 md:px-10 pb-12">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="bg-[#d4af37] text-[#0d0d0d] text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">Compliance</span>
              <span className="text-[#888] text-xs">February 28, 2026</span>
              <span className="text-[#888] text-xs">•</span>
              <span className="text-[#888] text-xs">6 min read</span>
              <span className="text-[#888] text-xs">•</span>
              <span className="text-[#888] text-xs">By Dr. Osmel Villarreal, DBA</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-black text-white leading-tight">
              Top 10 Compliance Tips Every<br className="hidden md:block" />
              <span className="text-[#d4af37]"> Home Health Agency Must Know in 2026</span>
            </h1>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 py-16">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Main */}
          <article className="flex-1 min-w-0">
            <p className="text-[#bbb] text-lg leading-relaxed mb-8 border-l-4 border-[#d4af37] pl-6">
              Compliance is not a one-time event — it is an ongoing commitment that defines the quality and sustainability of your home health agency. In 2026, regulatory scrutiny is higher than ever, and agencies that are not proactively managing compliance risk costly penalties, survey failures, and loss of accreditation.
            </p>
            <p className="text-[#999] leading-relaxed mb-12">
              Based on years of real-world experience and doctoral research in home health operations, Dr. Osmel Villarreal, DBA, shares the top 10 compliance strategies that every home health agency must implement to stay survey-ready and operationally strong.
            </p>

            {/* Tips */}
            <div className="flex flex-col gap-8">
              {tips.map((tip, i) => (
                <div key={i} className="flex gap-6 group">
                  <div className="flex-shrink-0 pt-1">
                    <span className="text-[#d4af37]/40 font-black text-3xl leading-none">{tip.num}</span>
                  </div>
                  <div className="border-l border-[#1e1e1e] pl-6 group-hover:border-[#d4af37]/30 transition-colors duration-300">
                    <h2 className="text-white font-bold text-lg mb-3">{tip.title}</h2>
                    <p className="text-[#888] text-sm leading-relaxed">{tip.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Summary Box */}
            <div className="bg-[#111] border border-[#d4af37]/20 rounded-xl p-8 mt-14">
              <h3 className="text-[#d4af37] font-bold text-sm uppercase tracking-widest mb-5">The Bottom Line</h3>
              <p className="text-[#aaa] leading-relaxed mb-4">
                Compliance is not about passing a survey — it is about building an agency that delivers consistent, high-quality care every single day. The agencies that thrive long-term are those that embed compliance into their culture, systems, and daily operations.
              </p>
              <p className="text-[#aaa] leading-relaxed">
                If your agency is struggling with any of these areas, Advantixx can help. Our ACHC Certified Consultant provides hands-on, evidence-based guidance to get your agency survey-ready and keep it there.
              </p>
            </div>

            {/* CTA */}
            <div className="bg-gradient-to-br from-[#d4af37]/10 to-[#d4af37]/5 border border-[#d4af37]/30 rounded-xl p-8 text-center mt-10">
              <h3 className="text-white font-black text-xl mb-3">Is Your Agency Truly Compliance-Ready?</h3>
              <p className="text-[#888] text-sm mb-6 leading-relaxed">
                Schedule a compliance assessment with Dr. Villarreal and find out exactly where your agency stands — and how to fix it fast.
              </p>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 bg-[#d4af37] text-[#0d0d0d] font-bold text-sm px-8 py-3.5 rounded-full hover:bg-[#f4c430] transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                Schedule a Compliance Assessment
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </article>

          {/* Sidebar */}
          <aside className="lg:w-72 flex-shrink-0">
            <div className="sticky top-28 flex flex-col gap-6">
              <div className="bg-[#111] border border-[#1e1e1e] rounded-xl p-6">
                <p className="text-[#d4af37] text-xs uppercase tracking-widest font-semibold mb-4">About the Author</p>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/30 flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-line text-[#d4af37] text-lg"></i>
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm">Dr. Osmel Villarreal</p>
                    <p className="text-[#666] text-xs">DBA, MBA, MS</p>
                  </div>
                </div>
                <p className="text-[#777] text-xs leading-relaxed">
                  ACHC Certified Consultant with doctoral research expertise in home health operations, compliance, and management systems.
                </p>
                <Link to="/why-dr-villarreal" className="mt-4 flex items-center gap-1.5 text-[#d4af37] text-xs font-semibold hover:gap-2.5 transition-all duration-200 whitespace-nowrap cursor-pointer">
                  Learn More <i className="ri-arrow-right-line"></i>
                </Link>
              </div>

              <div className="bg-[#111] border border-[#1e1e1e] rounded-xl p-6">
                <p className="text-[#d4af37] text-xs uppercase tracking-widest font-semibold mb-5">Related Articles</p>
                <div className="flex flex-col gap-5">
                  {[
                    { title: 'Complete ACHC Accreditation Guide', path: '/blog/achc-accreditation-guide' },
                    { title: 'How to Prepare for a Mock Survey', path: '/blog/mock-survey-preparation' },
                    { title: 'ACHC Accreditation FAQ', path: '/faq' },
                  ].map((post, i) => (
                    <Link key={i} to={post.path} className="group cursor-pointer">
                      <p className="text-[#aaa] text-sm leading-snug group-hover:text-[#d4af37] transition-colors duration-200">{post.title}</p>
                      <span className="text-[#555] text-xs mt-1 flex items-center gap-1 group-hover:gap-2 transition-all duration-200">Read <i className="ri-arrow-right-line text-xs"></i></span>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="bg-gradient-to-br from-[#d4af37]/15 to-[#d4af37]/5 border border-[#d4af37]/30 rounded-xl p-6 text-center">
                <i className="ri-calendar-check-line text-[#d4af37] text-3xl mb-3 block"></i>
                <p className="text-white font-bold text-sm mb-2">Free Consultation</p>
                <p className="text-[#777] text-xs mb-4 leading-relaxed">Get your personalized compliance roadmap today.</p>
                <Link to="/services" className="block bg-[#d4af37] text-[#0d0d0d] font-bold text-xs px-5 py-2.5 rounded-full hover:bg-[#f4c430] transition-colors duration-200 whitespace-nowrap cursor-pointer">
                  Book Now
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6 md:px-10 pb-16">
        <Link to="/blog" className="inline-flex items-center gap-2 text-[#888] hover:text-[#d4af37] text-sm transition-colors duration-200 cursor-pointer">
          <i className="ri-arrow-left-line"></i>
          Back to All Articles
        </Link>
      </div>

      <Footer />
    </div>
  );
}
